import { getAtlasMorphology, type AtlasMorphologySource } from '@/data/grass-atlas-morphology'
import {
  COOL_SEASON_MODEL_SOURCES,
  COOL_SEASON_PROCEDURAL_MODEL_SPECS,
} from './cool-season-models'
import {
  WARM_RUNNER_MODEL_SPECS,
  WARM_RUNNER_MODEL_SOURCES,
} from './warm-runner-models'
import { DISTINCT_WARM_MODEL_SPECS } from './distinct-warm-models'
import type { ProceduralGrassModelRegistry, ProceduralGrassModelSpec } from './model-contract'

const basePalette = {
  leaf: 0x4a7945,
  leafDark: 0x285437,
  vein: 0x759662,
  sheath: 0x668851,
  runner: 0x977449,
  root: 0xc3a36b,
  seed: 0x9b8f58,
}

function coolSources(slug: keyof typeof COOL_SEASON_PROCEDURAL_MODEL_SPECS): AtlasMorphologySource[] {
  const spec = COOL_SEASON_PROCEDURAL_MODEL_SPECS[slug]
  return spec.sourceIds.map((id) => {
    const source = Object.values(COOL_SEASON_MODEL_SOURCES).find((candidate) => candidate.id === id)
    if (!source) throw new Error(`Missing cool-season model source: ${id}`)
    return { publisher: source.publisher, label: source.title, href: source.href }
  })
}

function warmSources(slug: keyof typeof WARM_RUNNER_MODEL_SPECS): AtlasMorphologySource[] {
  return WARM_RUNNER_MODEL_SPECS[slug].sources.map((id) => {
    const source = WARM_RUNNER_MODEL_SOURCES.find((candidate) => candidate.id === id)
    if (!source) throw new Error(`Missing warm-runner model source: ${id}`)
    return { publisher: source.publisher, label: source.title, href: source.href }
  })
}

function distinctSources(slug: keyof typeof DISTINCT_WARM_MODEL_SPECS): AtlasMorphologySource[] {
  return DISTINCT_WARM_MODEL_SPECS[slug].sourceRecords.map((source) => ({
    publisher: source.publisher,
    label: source.title,
    href: source.href,
  }))
}

function defineSpec(spec: ProceduralGrassModelSpec) {
  return spec
}

export const GRASS_MODEL_REGISTRY: ProceduralGrassModelRegistry = {
  'tall-fescue': defineSpec({
    slug: 'tall-fescue',
    displayName: 'Tall Fescue',
    scientificName: 'Schedonorus arundinaceus',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x426d3c, leafDark: 0x244c31, vein: 0x779069 },
    blades: {
      count: 10,
      width: 0.5,
      minHeight: 2.3,
      maxHeight: 4.05,
      posture: 'upright',
      tip: 'pointed',
      surface: 'ridged',
      vernation: 'rolled',
      ridgeCount: 5,
      gloss: 0.08,
      hairDensity: 0,
    },
    collar: { ligule: 'short-membrane', auricles: 'withheld', sheath: 'round' },
    growth: { form: 'bunch-rhizome', runnerThickness: 0.055, nodeSpacing: 0.7, daughterShoots: 1, rhizomeDepth: 0.48 },
    roots: { depth: 2.35, density: 20, spread: 0.85 },
    seedhead: { form: 'panicle', height: 5.75, branchCount: 7, spikeletCount: 28, awnLength: 0 },
    sources: coolSources('tall-fescue'),
    limitations: COOL_SEASON_PROCEDURAL_MODEL_SPECS['tall-fescue'].uncertainties.slice(),
  }),
  'fine-fescue': defineSpec({
    slug: 'fine-fescue',
    displayName: 'Fine Fescue Group',
    scientificName: 'Festuca spp.',
    scope: 'species-group',
    palette: { ...basePalette, leaf: 0x4c7650, leafDark: 0x2a5036, vein: 0x69856c },
    blades: {
      count: 18,
      width: 0.12,
      minHeight: 2.1,
      maxHeight: 3.55,
      posture: 'tufted',
      tip: 'boat',
      surface: 'smooth',
      vernation: 'folded',
      ridgeCount: 1,
      gloss: 0.02,
      hairDensity: 0,
    },
    collar: { ligule: 'short-membrane', auricles: 'absent', sheath: 'round' },
    growth: { form: 'bunch-rhizome', runnerThickness: 0.035, nodeSpacing: 0.58, daughterShoots: 1, rhizomeDepth: 0.34 },
    roots: { depth: 1.55, density: 18, spread: 0.62 },
    seedhead: { form: 'panicle', height: 5.2, branchCount: 6, spikeletCount: 20, awnLength: 0 },
    sources: coolSources('fine-fescue'),
    limitations: COOL_SEASON_PROCEDURAL_MODEL_SPECS['fine-fescue'].uncertainties.slice(),
  }),
  'perennial-ryegrass': defineSpec({
    slug: 'perennial-ryegrass',
    displayName: 'Perennial Ryegrass',
    scientificName: 'Lolium perenne',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x386943, leafDark: 0x1d4731, vein: 0x6d956a },
    blades: {
      count: 11,
      width: 0.24,
      minHeight: 2.15,
      maxHeight: 3.75,
      posture: 'upright',
      tip: 'boat',
      surface: 'glossy',
      vernation: 'folded',
      ridgeCount: 3,
      gloss: 0.88,
      hairDensity: 0,
    },
    collar: { ligule: 'short-membrane', auricles: 'rudimentary', sheath: 'flattened' },
    growth: { form: 'bunch', runnerThickness: 0.04, nodeSpacing: 0.6, daughterShoots: 2, rhizomeDepth: 0.3 },
    roots: { depth: 1.5, density: 15, spread: 0.65 },
    seedhead: { form: 'spike', height: 5.3, branchCount: 0, spikeletCount: 12, awnLength: 0 },
    sources: coolSources('perennial-ryegrass'),
    limitations: COOL_SEASON_PROCEDURAL_MODEL_SPECS['perennial-ryegrass'].uncertainties.slice(),
  }),
  'annual-ryegrass': defineSpec({
    slug: 'annual-ryegrass',
    displayName: 'Annual Ryegrass',
    scientificName: 'Lolium multiflorum',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x4a7748, leafDark: 0x265039, vein: 0x7ba070 },
    blades: {
      count: 9,
      width: 0.3,
      minHeight: 2.2,
      maxHeight: 3.9,
      posture: 'upright',
      tip: 'boat',
      surface: 'glossy',
      vernation: 'rolled',
      ridgeCount: 3,
      gloss: 0.78,
      hairDensity: 0,
    },
    collar: { ligule: 'short-membrane', auricles: 'clasping', sheath: 'round' },
    growth: { form: 'bunch', runnerThickness: 0.04, nodeSpacing: 0.6, daughterShoots: 2, rhizomeDepth: 0.3 },
    roots: { depth: 1.35, density: 13, spread: 0.62 },
    seedhead: { form: 'spike', height: 5.5, branchCount: 0, spikeletCount: 13, awnLength: 0 },
    sources: coolSources('annual-ryegrass'),
    limitations: COOL_SEASON_PROCEDURAL_MODEL_SPECS['annual-ryegrass'].uncertainties.slice(),
  }),
  bermudagrass: defineSpec({
    slug: 'bermudagrass',
    displayName: 'Bermudagrass',
    scientificName: 'Cynodon dactylon',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x527c35, leafDark: 0x2d582b, vein: 0x86a75d },
    blades: {
      count: 16,
      width: 0.17,
      minHeight: 1.35,
      maxHeight: 2.65,
      posture: 'prostrate',
      tip: 'pointed',
      surface: 'smooth',
      vernation: 'mixed',
      ridgeCount: 1,
      gloss: 0.12,
      hairDensity: 0.18,
    },
    collar: { ligule: 'hair-fringe', auricles: 'absent', sheath: 'round' },
    growth: { form: 'both', runnerThickness: 0.045, nodeSpacing: 0.72, daughterShoots: 3, rhizomeDepth: 0.42 },
    roots: { depth: 1.65, density: 18, spread: 0.82 },
    seedhead: { form: 'digitate', height: 4.45, branchCount: 4, spikeletCount: 24, awnLength: 0 },
    sources: warmSources('bermudagrass'),
    limitations: WARM_RUNNER_MODEL_SPECS.bermudagrass.uncertainty.slice(),
  }),
  zoysiagrass: defineSpec({
    slug: 'zoysiagrass',
    displayName: 'Zoysiagrass Group',
    scientificName: 'Zoysia spp.',
    scope: 'species-group',
    palette: { ...basePalette, leaf: 0x557b35, leafDark: 0x2d542d, vein: 0x819d5b },
    blades: {
      count: 18,
      width: 0.31,
      minHeight: 1.8,
      maxHeight: 3.15,
      posture: 'upright',
      tip: 'pointed',
      surface: 'hairy',
      vernation: 'rolled',
      ridgeCount: 2,
      gloss: 0.05,
      hairDensity: 0.48,
    },
    collar: { ligule: 'hair-fringe', auricles: 'absent', sheath: 'round' },
    growth: { form: 'both', runnerThickness: 0.065, nodeSpacing: 0.62, daughterShoots: 3, rhizomeDepth: 0.38 },
    roots: { depth: 1.22, density: 24, spread: 0.8 },
    seedhead: { form: 'spike', height: 4.65, branchCount: 0, spikeletCount: 12, awnLength: 0 },
    sources: warmSources('zoysiagrass'),
    limitations: WARM_RUNNER_MODEL_SPECS.zoysiagrass.uncertainty.slice(),
  }),
  'st-augustinegrass': defineSpec({
    slug: 'st-augustinegrass',
    displayName: 'St. Augustinegrass',
    scientificName: 'Stenotaphrum secundatum',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x557b39, leafDark: 0x2c5631, vein: 0x89a56c, runner: 0x6f6d3c },
    blades: {
      count: 9,
      width: 0.6,
      minHeight: 1.85,
      maxHeight: 3.25,
      posture: 'arching',
      tip: 'blunt',
      surface: 'smooth',
      vernation: 'folded',
      ridgeCount: 1,
      gloss: 0.08,
      hairDensity: 0,
    },
    collar: { ligule: 'hair-fringe', auricles: 'absent', sheath: 'flattened' },
    growth: { form: 'stolon', runnerThickness: 0.105, nodeSpacing: 0.9, daughterShoots: 3, rhizomeDepth: 0.3 },
    roots: { depth: 1.45, density: 16, spread: 0.78 },
    seedhead: { form: 'spike', height: 4.75, branchCount: 0, spikeletCount: 10, awnLength: 0 },
    sources: warmSources('st-augustinegrass'),
    limitations: WARM_RUNNER_MODEL_SPECS['st-augustinegrass'].uncertainty.slice(),
  }),
  centipede: defineSpec({
    slug: 'centipede',
    displayName: 'Centipedegrass',
    scientificName: 'Eremochloa ophiuroides',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x739342, leafDark: 0x3e642f, vein: 0x9aac63 },
    blades: {
      count: 12,
      width: 0.34,
      minHeight: 1.45,
      maxHeight: 2.75,
      posture: 'prostrate',
      tip: 'blunt',
      surface: 'hairy',
      vernation: 'folded',
      ridgeCount: 1,
      gloss: 0.03,
      hairDensity: 0.35,
    },
    collar: { ligule: 'hair-fringe', auricles: 'absent', sheath: 'flattened' },
    growth: { form: 'stolon', runnerThickness: 0.055, nodeSpacing: 0.7, daughterShoots: 3, rhizomeDepth: 0.28 },
    roots: { depth: 1.05, density: 13, spread: 0.72 },
    seedhead: { form: 'raceme', height: 4.15, branchCount: 0, spikeletCount: 11, awnLength: 0 },
    sources: warmSources('centipedegrass'),
    limitations: WARM_RUNNER_MODEL_SPECS.centipedegrass.uncertainty.slice(),
  }),
  kikuyugrass: defineSpec({
    slug: 'kikuyugrass',
    displayName: 'Kikuyugrass',
    scientificName: 'Cenchrus clandestinus',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x6c9347, leafDark: 0x3c6434, vein: 0x9aad72, runner: 0x7e6840 },
    blades: {
      count: 13,
      width: 0.37,
      minHeight: 1.65,
      maxHeight: 3.05,
      posture: 'prostrate',
      tip: 'pointed',
      surface: 'hairy',
      vernation: 'mixed',
      ridgeCount: 2,
      gloss: 0.04,
      hairDensity: 0.42,
    },
    collar: { ligule: 'hair-fringe', auricles: 'absent', sheath: 'flattened' },
    growth: { form: 'both', runnerThickness: 0.1, nodeSpacing: 0.85, daughterShoots: 4, rhizomeDepth: 0.58 },
    roots: { depth: 1.55, density: 18, spread: 0.82 },
    seedhead: { form: 'hidden', height: 3.8, branchCount: 0, spikeletCount: 0, awnLength: 0 },
    sources: distinctSources('kikuyugrass'),
    limitations: DISTINCT_WARM_MODEL_SPECS.kikuyugrass.uncertaintyAndReview.slice(),
  }),
  bahiagrass: defineSpec({
    slug: 'bahiagrass',
    displayName: 'Bahiagrass',
    scientificName: 'Paspalum notatum',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x597740, leafDark: 0x304f34, vein: 0x849665, runner: 0x7d6650 },
    blades: {
      count: 9,
      width: 0.5,
      minHeight: 2.1,
      maxHeight: 3.65,
      posture: 'tufted',
      tip: 'pointed',
      surface: 'ridged',
      vernation: 'rolled',
      ridgeCount: 4,
      gloss: 0.03,
      hairDensity: 0.12,
    },
    collar: { ligule: 'hair-fringe', auricles: 'absent', sheath: 'flattened' },
    growth: { form: 'bunch-rhizome', runnerThickness: 0.11, nodeSpacing: 0.82, daughterShoots: 2, rhizomeDepth: 0.43 },
    roots: { depth: 2.25, density: 18, spread: 0.88 },
    seedhead: { form: 'y-fork', height: 5.8, branchCount: 2, spikeletCount: 18, awnLength: 0 },
    sources: distinctSources('bahiagrass'),
    limitations: DISTINCT_WARM_MODEL_SPECS.bahiagrass.uncertaintyAndReview.slice(),
  }),
  buffalograss: defineSpec({
    slug: 'buffalograss',
    displayName: 'Buffalograss',
    scientificName: 'Bouteloua dactyloides',
    scope: 'species',
    palette: { ...basePalette, leaf: 0x74815b, leafDark: 0x495942, vein: 0x9ba082, runner: 0x806f52 },
    blades: {
      count: 13,
      width: 0.2,
      minHeight: 1.35,
      maxHeight: 2.7,
      posture: 'tufted',
      tip: 'pointed',
      surface: 'hairy',
      vernation: 'rolled',
      ridgeCount: 2,
      gloss: 0.02,
      hairDensity: 0.72,
    },
    collar: { ligule: 'hair-fringe', auricles: 'absent', sheath: 'round' },
    growth: { form: 'stolon', runnerThickness: 0.045, nodeSpacing: 0.72, daughterShoots: 3, rhizomeDepth: 0.28 },
    roots: { depth: 2.15, density: 14, spread: 0.8 },
    seedhead: { form: 'buffalo', height: 4.3, branchCount: 2, spikeletCount: 8, awnLength: 0 },
    sources: distinctSources('buffalograss'),
    limitations: DISTINCT_WARM_MODEL_SPECS.buffalograss.uncertaintyAndReview.slice(),
  }),
  'mixed-cool-season-blend': defineSpec({
    slug: 'mixed-cool-season-blend',
    displayName: 'Example Cool-Season Mixed Stand',
    scientificName: 'Multiple cool-season species',
    scope: 'mixture',
    palette: { ...basePalette, leaf: 0x4b7749, leafDark: 0x2b5337, vein: 0x83a071 },
    blades: {
      count: 15,
      width: 0.32,
      minHeight: 1.95,
      maxHeight: 3.9,
      posture: 'tufted',
      tip: 'mixed',
      surface: 'glossy',
      vernation: 'mixed',
      ridgeCount: 3,
      gloss: 0.42,
      hairDensity: 0,
    },
    collar: { ligule: 'mixed', auricles: 'mixed', sheath: 'mixed' },
    growth: { form: 'mixed', runnerThickness: 0.05, nodeSpacing: 0.65, daughterShoots: 2, rhizomeDepth: 0.4 },
    roots: { depth: 1.7, density: 19, spread: 0.82 },
    seedhead: { form: 'mixed', height: 5.45, branchCount: 6, spikeletCount: 20, awnLength: 0 },
    sources: getAtlasMorphology('mixed-cool-season-blend').sources,
    limitations: [
      'This is an example stand made from separate shoots, not one plant or a fixed seed formula.',
      'Separate several shoots before comparing blade tips, collars, vernation, or runners.',
    ],
  }),
}

export function getProceduralGrassModel(slug: string) {
  return GRASS_MODEL_REGISTRY[slug]
}
